import React, { useState, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useSearchParams } from 'react-router-dom';
import { getDocumentRequest, submitSignedDocument, getCompanyById, getPreparerById, getDocumentTemplateById, createDocumentRequest } from '../firestoreHelper';
import { toEasternISOString } from '../dateHelper';

// Set worker source for react-pdf v10
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * PDF Document Filler - Client-facing component
 * Displays PDF with fillable fields for client to complete and sign
 */
function PDFDocumentFiller() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [documentData, setDocumentData] = useState(null);
  const [company, setCompany] = useState(null);
  const [preparer, setPreparer] = useState(null);
  
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [fieldValues, setFieldValues] = useState({});
  const [signatures, setSignatures] = useState({});
  const [typedSignatures, setTypedSignatures] = useState({});
  
  const [signatureModal, setSignatureModal] = useState({ isOpen: false, fieldId: null, type: null }); // modal state
  const [modalSignatureMode, setModalSignatureMode] = useState('draw');
  const [modalTypedValue, setModalTypedValue] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [createdRequestId, setCreatedRequestId] = useState(null);
  const modalCanvasRef = useRef(null);
  const containerRef = useRef(null);

  const requestId = searchParams.get('id');
  const templateId = searchParams.get('template');
  const effectiveRequestId = requestId || createdRequestId;

  // Detect mobile
  const isMobile = window.innerWidth <= 768;

  // Load document request or template
  useEffect(() => {
    const loadDocument = async () => {
      // If we have a template ID, load the template directly
      if (templateId) {
        try {
          const template = await getDocumentTemplateById(templateId);
          if (!template) {
            setError('Form template not found or has been removed.');
            setLoading(false);
            return;
          }

          // Create a document request on-the-fly so submission works
          const request = await createDocumentRequest({
            templateId: template.id,
            template: template,
            templateData: template,
            clientName: '',
            clientEmail: '',
            preparerId: template.preparerId || '',
            companyId: template.companyId || '',
          });

          // Store the created request ID so submission works
          setCreatedRequestId(request.id);
          // Update URL so reloads work with the request ID
          window.history.replaceState(null, '', `${window.location.pathname}?id=${request.id}`);

          setDocumentData(request);

          // Initialize field values
          const initialValues = {};
          (template.fields || []).forEach(field => {
            initialValues[field.id] = field.type === 'checkbox' ? false : '';
          });
          setFieldValues(initialValues);

          // Load company and preparer
          if (template.companyId) {
            const companyData = await getCompanyById(template.companyId);
            setCompany(companyData);
          }
          if (template.preparerId) {
            const preparerData = await getPreparerById(template.preparerId);
            setPreparer(preparerData);
          }

          setLoading(false);
          return;
        } catch (err) {
          console.error('Error loading template:', err);
          setError('Failed to load form. Please try again.');
          setLoading(false);
          return;
        }
      }

      if (!requestId) {
        setError('Invalid document link. Please contact your tax preparer.');
        setLoading(false);
        return;
      }

      try {
        const request = await getDocumentRequest(requestId);
        if (!request) {
          setError('Document not found or has expired.');
          setLoading(false);
          return;
        }

        if (request.status === 'signed') {
          setSubmitted(true);
          setDocumentData(request);
          setLoading(false);
          return;
        }

        setDocumentData(request);

        // Initialize field values - preparer fields use pre-filled values
        const initialValues = {};
        const preparerValues = request.preparerFieldValues || {};
        (request.templateData?.fields || request.template?.fields || []).forEach(field => {
          if (field.filledBy === 'preparer' && preparerValues[field.id] !== undefined) {
            // Use preparer's pre-filled value
            initialValues[field.id] = preparerValues[field.id];
          } else {
            initialValues[field.id] = field.type === 'checkbox' ? false : '';
          }
        });
        setFieldValues(initialValues);

        // Load company and preparer
        if (request.companyId) {
          const companyData = await getCompanyById(request.companyId);
          setCompany(companyData);
        }
        if (request.preparerId) {
          const preparerData = await getPreparerById(request.preparerId);
          setPreparer(preparerData);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error loading document:', err);
        setError('Failed to load document. Please try again.');
        setLoading(false);
      }
    };

    loadDocument();
  }, [requestId, templateId]);

  // Adjust scale for mobile
  useEffect(() => {
    if (isMobile && containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth - 20;
      setScale(containerWidth / 612); // 612 is standard PDF width in points
    }
  }, [isMobile]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  // Handle field value change with auto-fill for same field types
  const handleFieldChange = (fieldId, value) => {
    const template = documentData?.templateData || documentData?.template;
    const currentField = template?.fields?.find(f => f.id === fieldId);
    
    if (currentField && value) {
      // Auto-fill other fields of the same type that are empty
      const sameTypeFields = template.fields.filter(f => 
        f.id !== fieldId && 
        f.type === currentField.type && 
        f.filledBy !== 'preparer'
      );
      
      const updates = { [fieldId]: value };
      sameTypeFields.forEach(field => {
        // Only auto-fill if the field is currently empty
        if (!fieldValues[field.id]?.trim()) {
          updates[field.id] = value;
        }
      });
      
      setFieldValues(prev => ({ ...prev, ...updates }));
    } else {
      setFieldValues(prev => ({ ...prev, [fieldId]: value }));
    }
  };

  // Modal signature drawing functions
  const startModalDrawing = (e) => {
    setIsDrawing(true);
    const canvas = modalCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const drawModal = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = modalCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#1e3a8a';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopModalDrawing = () => {
    setIsDrawing(false);
  };

  const clearModalCanvas = () => {
    const canvas = modalCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const saveModalSignature = () => {
    if (!signatureModal) return;
    
    if (modalSignatureMode === 'draw') {
      const canvas = modalCanvasRef.current;
      if (canvas) {
        const dataUrl = canvas.toDataURL();
        setSignatures(prev => ({ ...prev, [signatureModal.fieldId]: dataUrl }));
        
        // Auto-fill same type signature fields
        const template = documentData?.templateData || documentData?.template;
        const sameTypeFields = template?.fields?.filter(f => 
          f.id !== signatureModal.fieldId && 
          f.type === signatureModal.type && 
          f.filledBy !== 'preparer' &&
          !signatures[f.id]
        ) || [];
        
        sameTypeFields.forEach(field => {
          setSignatures(prev => ({ ...prev, [field.id]: dataUrl }));
        });
      }
    } else {
      if (modalTypedValue.trim()) {
        setTypedSignatures(prev => ({ ...prev, [signatureModal.fieldId]: modalTypedValue }));
        
        // Auto-fill same type signature fields
        const template = documentData?.templateData || documentData?.template;
        const sameTypeFields = template?.fields?.filter(f => 
          f.id !== signatureModal.fieldId && 
          f.type === signatureModal.type && 
          f.filledBy !== 'preparer' &&
          !typedSignatures[f.id]?.trim()
        ) || [];
        
        sameTypeFields.forEach(field => {
          setTypedSignatures(prev => ({ ...prev, [field.id]: modalTypedValue }));
        });
      }
    }
    
    setSignatureModal({ isOpen: false, fieldId: null, type: null });
    setModalTypedValue('');
    setModalSignatureMode('draw');
  };

  const openSignatureModal = (field) => {
    setSignatureModal({ isOpen: true, fieldId: field.id, type: field.type });
    setModalSignatureMode('draw');
    setModalTypedValue('');
  };

  // Clear signature
  const clearSignature = (fieldId) => {
    setSignatures(prev => {
      const updated = { ...prev };
      delete updated[fieldId];
      return updated;
    });
    setTypedSignatures(prev => {
      const updated = { ...prev };
      delete updated[fieldId];
      return updated;
    });
  };

  // Validate all required fields (only client fields need validation)
  const validateFields = () => {
    const template = documentData?.templateData || documentData?.template;
    if (!template?.fields) return true;

    // Only validate client fields (preparer fields are already filled)
    const clientFields = template.fields.filter(f => f.filledBy !== 'preparer');
    
    for (const field of clientFields) {
      if (field.required) {
        if (field.type === 'signature' || field.type === 'initials') {
          // Check both drawn and typed signatures
          if (!signatures[field.id] && !typedSignatures[field.id]?.trim()) {
            alert(`Please provide your ${field.label.toLowerCase()} (draw or type)`);
            setCurrentPage(field.page);
            return false;
          }
        } else if (field.type === 'checkbox') {
          if (!fieldValues[field.id]) {
            alert(`Please check the ${field.label} field`);
            setCurrentPage(field.page);
            return false;
          }
        } else {
          if (!fieldValues[field.id]?.trim()) {
            alert(`Please fill in ${field.label}`);
            setCurrentPage(field.page);
            return false;
          }
        }
      }
    }
    return true;
  };

  // Submit signed document
  const handleSubmit = async () => {
    if (!validateFields()) return;

    if (!effectiveRequestId) {
      alert('Unable to submit — document request not found. Please refresh and try again.');
      return;
    }

    setSubmitting(true);
    try {
      const signedData = {
        requestId: effectiveRequestId,
        fieldValues,
        signatures,
        typedSignatures,
        signedAt: toEasternISOString(),
        clientInfo: {
          name: fieldValues.name || fieldValues.fullName || typedSignatures[Object.keys(typedSignatures)[0]] || '',
          email: fieldValues.email || ''
        }
      };

      await submitSignedDocument(effectiveRequestId, signedData);
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting document:', err);
      alert('Failed to submit document. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Render field input based on type
  const renderFieldInput = (field) => {
    const isPreparerField = field.filledBy === 'preparer';
    
    const baseStyle = {
      position: 'absolute',
      left: field.x * scale,
      top: field.y * scale,
      width: field.width * scale,
      height: field.height * scale,
      zIndex: 10
    };

    // Preparer fields are read-only with different styling
    const preparerStyle = isPreparerField ? {
      backgroundColor: '#f5f3ff',
      borderColor: '#8b5cf6',
      cursor: 'not-allowed',
      opacity: 0.9
    } : {};

    switch (field.type) {
      case 'signature':
      case 'initials':
        // Preparer signature/initials are shown as text (borderless)
        if (isPreparerField) {
          return (
            <div key={field.id} style={{ 
              ...baseStyle, 
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: '"Brush Script MT", cursive',
              fontSize: Math.min(18, field.height * scale * 0.6) + 'px',
              color: '#6d28d9',
              borderBottom: '1px solid #ddd'
            }}>
              {fieldValues[field.id] || ''}
            </div>
          );
        }
        
        const hasSignature = signatures[field.id] || typedSignatures[field.id]?.trim();
        
        // Borderless signature field - click to open modal
        return (
          <div 
            key={field.id} 
            onClick={() => !hasSignature && openSignatureModal(field)}
            style={{ 
              ...baseStyle,
              backgroundColor: hasSignature ? 'transparent' : 'rgba(139, 92, 246, 0.05)',
              borderBottom: hasSignature ? 'none' : '2px dotted #8b5cf6',
              cursor: hasSignature ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
          >
            {signatures[field.id] ? (
              <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                <img src={signatures[field.id]} alt="Signature" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                <button 
                  onClick={(e) => { e.stopPropagation(); clearSignature(field.id); }} 
                  style={{ position: 'absolute', top: '-8px', right: '-8px', width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#ef4444', color: 'white', border: 'none', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >×</button>
              </div>
            ) : typedSignatures[field.id]?.trim() ? (
              <div style={{ width: '100%', height: '100%', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontFamily: '"Brush Script MT", cursive', fontSize: Math.min(22, field.height * scale * 0.6) + 'px', color: '#1e3a8a' }}>
                  {typedSignatures[field.id]}
                </span>
                <button 
                  onClick={(e) => { e.stopPropagation(); setTypedSignatures(prev => ({ ...prev, [field.id]: '' })); }} 
                  style={{ position: 'absolute', top: '-8px', right: '-8px', width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#ef4444', color: 'white', border: 'none', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >×</button>
              </div>
            ) : (
              <span style={{ color: '#8b5cf6', fontSize: '11px', opacity: 0.8 }}>
                {field.type === 'initials' ? '✍️ Click for initials' : '✍️ Click to sign'}
              </span>
            )}
          </div>
        );

      case 'checkbox':
        return (
          <div
            key={field.id}
            onClick={() => {
              if (!isPreparerField) {
                handleFieldChange(field.id, !fieldValues[field.id]);
              }
            }}
            style={{
              ...baseStyle,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: isPreparerField ? 'not-allowed' : 'pointer',
              backgroundColor: 'transparent'
            }}
          >
            <div
              style={{
                width: Math.max(14, Math.min(field.width, field.height) * scale * 0.8),
                height: Math.max(14, Math.min(field.width, field.height) * scale * 0.8),
                backgroundColor: fieldValues[field.id] ? (field.color || '#10b981') : 'white',
                border: `1px solid ${fieldValues[field.id] ? (field.color || '#10b981') : '#cbd5e1'}`,
                borderRadius: '2px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.15s ease'
              }}
            >
              {fieldValues[field.id] && (
                <span style={{ color: 'white', fontWeight: 'bold', fontSize: Math.max(10, Math.min(field.width, field.height) * scale * 0.5) }}>
                  ✓
                </span>
              )}
            </div>
          </div>
        );

      case 'date':
        return (
          <input
            key={field.id}
            type={isPreparerField ? 'text' : 'date'}
            value={fieldValues[field.id] || ''}
            onChange={(e) => !isPreparerField && handleFieldChange(field.id, e.target.value)}
            readOnly={isPreparerField}
            style={{
              ...baseStyle,
              border: 'none',
              borderBottom: isPreparerField ? 'none' : '1px solid #cbd5e1',
              backgroundColor: 'transparent',
              outline: 'none',
              fontSize: Math.min(14, field.height * scale * 0.6) + 'px',
              color: '#1e293b',
              padding: '2px 4px',
              ...preparerStyle
            }}
          />
        );

      case 'ssn':
        return (
          <input
            key={field.id}
            type="text"
            value={fieldValues[field.id] || ''}
            onChange={(e) => {
              if (isPreparerField) return;
              const val = e.target.value.replace(/\D/g, '').slice(0, 4);
              handleFieldChange(field.id, val);
            }}
            placeholder={isPreparerField ? '' : '••••'}
            maxLength={4}
            readOnly={isPreparerField}
            style={{
              ...baseStyle,
              border: 'none',
              borderBottom: isPreparerField ? 'none' : '1px solid #cbd5e1',
              backgroundColor: 'transparent',
              outline: 'none',
              fontSize: Math.min(14, field.height * scale * 0.6) + 'px',
              color: '#1e293b',
              padding: '2px 4px',
              letterSpacing: '2px',
              ...preparerStyle
            }}
          />
        );

      default:
        return (
          <input
            key={field.id}
            type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
            value={fieldValues[field.id] || ''}
            onChange={(e) => !isPreparerField && handleFieldChange(field.id, e.target.value)}
            placeholder={isPreparerField ? '' : (field.placeholder || '')}
            readOnly={isPreparerField}
            style={{
              ...baseStyle,
              border: 'none',
              borderBottom: isPreparerField ? 'none' : '1px solid #cbd5e1',
              backgroundColor: isPreparerField ? 'transparent' : (fieldValues[field.id] ? 'transparent' : 'rgba(59, 130, 246, 0.03)'),
              outline: 'none',
              fontSize: Math.min(14, field.height * scale * 0.6) + 'px',
              color: '#1e293b',
              padding: '2px 4px',
              transition: 'all 0.2s ease',
              ...preparerStyle
            }}
          />
        );
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingCard}>
          <div style={styles.spinner}></div>
          <p>Loading document...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorCard}>
          <h2>⚠️ Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <div style={styles.container}>
        <div style={styles.successCard}>
          <div style={styles.successIcon}>✅</div>
          <h2>Document Signed Successfully!</h2>
          <p>Thank you for completing the document.</p>
          <p style={styles.successSubtext}>
            A copy has been sent to your tax preparer. You may close this page.
          </p>
          {company && (
            <div style={styles.companyInfo}>
              <p><strong>{company.name}</strong></p>
              {company.phone && <p>📞 {company.phone}</p>}
              {company.email && <p>📧 {company.email}</p>}
            </div>
          )}
        </div>
      </div>
    );
  }

  const template = documentData?.templateData || documentData?.template;
  const currentPageFields = (template?.fields || []).filter(f => f.page === currentPage);
  
  // Calculate field progress
  const allClientFields = (template?.fields || []).filter(f => f.filledBy !== 'preparer');
  const requiredClientFields = allClientFields.filter(f => f.required);
  const filledRequiredFields = requiredClientFields.filter(f => {
    if (f.type === 'signature' || f.type === 'initials') return signatures[f.id] || typedSignatures[f.id]?.trim();
    if (f.type === 'checkbox') return fieldValues[f.id];
    return fieldValues[f.id]?.trim();
  });
  const progress = requiredClientFields.length > 0 
    ? Math.round((filledRequiredFields.length / requiredClientFields.length) * 100) 
    : 100;

  return (
    <div style={styles.container} ref={containerRef}>
      {/* Signature Modal */}
      {signatureModal.isOpen && (
        <div style={styles.modalOverlay}>
          <div style={styles.signatureModal}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>
                {signatureModal.type === 'initials' ? '✍️ Add Your Initials' : '✍️ Add Your Signature'}
              </h3>
              <button 
                onClick={() => setSignatureModal({ isOpen: false, fieldId: null, type: null })}
                style={styles.modalCloseBtn}
              >
                ✕
              </button>
            </div>
            
            {/* Mode Tabs */}
            <div style={styles.modalTabs}>
              <button
                onClick={() => setModalSignatureMode('draw')}
                style={{
                  ...styles.modalTab,
                  ...(modalSignatureMode === 'draw' ? styles.modalTabActive : {})
                }}
              >
                🖊️ Draw
              </button>
              <button
                onClick={() => setModalSignatureMode('type')}
                style={{
                  ...styles.modalTab,
                  ...(modalSignatureMode === 'type' ? styles.modalTabActive : {})
                }}
              >
                ⌨️ Type
              </button>
            </div>

            {/* Modal Content */}
            <div style={styles.modalContent}>
              {modalSignatureMode === 'draw' ? (
                <div style={styles.modalDrawArea}>
                  <canvas
                    ref={modalCanvasRef}
                    width={400}
                    height={150}
                    onMouseDown={startModalDrawing}
                    onMouseMove={drawModal}
                    onMouseUp={stopModalDrawing}
                    onMouseLeave={stopModalDrawing}
                    onTouchStart={startModalDrawing}
                    onTouchMove={drawModal}
                    onTouchEnd={stopModalDrawing}
                    style={styles.modalCanvas}
                  />
                  <button onClick={clearModalCanvas} style={styles.clearBtn}>
                    🗑️ Clear
                  </button>
                </div>
              ) : (
                <div style={styles.modalTypeArea}>
                  <input
                    type="text"
                    value={modalTypedValue}
                    onChange={(e) => setModalTypedValue(e.target.value)}
                    placeholder={signatureModal.type === 'initials' ? 'Type your initials...' : 'Type your name...'}
                    style={styles.modalTypeInput}
                    autoFocus
                  />
                  {modalTypedValue && (
                    <div style={styles.modalPreview}>
                      <span style={styles.modalPreviewText}>{modalTypedValue}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div style={styles.modalActions}>
              <button
                onClick={() => setSignatureModal({ isOpen: false, fieldId: null, type: null })}
                style={styles.modalCancelBtn}
              >
                Cancel
              </button>
              <button
                onClick={saveModalSignature}
                style={styles.modalDoneBtn}
              >
                ✅ Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          {company?.logo && <img src={company.logo} alt={company.name} style={styles.logo} />}
          <div style={styles.headerText}>
            <h1 style={styles.title}>{company?.name || 'Tax Services'}</h1>
            <p style={styles.subtitle}>{template?.name || 'Document'}</p>
          </div>
        </div>
        {preparer && (
          <div style={styles.preparerInfo}>
            <span style={styles.preparerLabel}>Sent by</span>
            <span style={styles.preparerName}>{preparer.name}</span>
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div style={styles.progressSection}>
        <div style={styles.progressHeader}>
          <span style={styles.progressLabel}>📝 Form Progress</span>
          <span style={styles.progressPercent}>{progress}% Complete</span>
        </div>
        <div style={styles.progressTrack}>
          <div style={{ ...styles.progressFill, width: `${progress}%` }}></div>
        </div>
        <div style={styles.fieldSummary}>
          <div style={styles.summaryItem}>
            <span style={styles.summaryIcon}>✅</span>
            <span>{filledRequiredFields.length} of {requiredClientFields.length} required fields</span>
          </div>
          <div style={styles.summaryItem}>
            <span style={styles.summaryIcon}>📄</span>
            <span>Page {currentPage} of {numPages || '?'}</span>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <span style={{ ...styles.legendDot, backgroundColor: '#3b82f6' }}></span>
          <span>Your fields to fill</span>
        </div>
        <div style={styles.legendItem}>
          <span style={{ ...styles.legendDot, backgroundColor: '#8b5cf6' }}></span>
          <span>Pre-filled by preparer</span>
        </div>
        <div style={styles.legendItem}>
          <span style={{ color: '#ef4444', fontWeight: 'bold' }}>*</span>
          <span>Required</span>
        </div>
      </div>

      {/* PDF Viewer */}
      <div style={styles.pdfContainer}>
        <div style={styles.pdfWrapper}>
          <Document
            file={template?.pdfData}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={<div style={styles.loading}>Loading PDF...</div>}
          >
            <Page
              pageNumber={currentPage}
              scale={scale}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>

          {/* Field Inputs Overlay */}
          {currentPageFields.map(field => renderFieldInput(field))}
        </div>
      </div>

      {/* Navigation & Submit */}
      <div style={styles.bottomBar}>
        <div style={styles.pageNav}>
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage <= 1}
            style={styles.navButton}
          >
            ← Previous
          </button>
          
          {currentPage < numPages ? (
            <button
              onClick={() => setCurrentPage(p => p + 1)}
              style={styles.nextButton}
            >
              Next Page →
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={styles.submitButton}
            >
              {submitting ? 'Submitting...' : '✅ Sign & Submit'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f1f5f9',
    display: 'flex',
    flexDirection: 'column'
  },
  loadingCard: {
    maxWidth: '400px',
    margin: '100px auto',
    padding: '40px',
    backgroundColor: 'white',
    borderRadius: '12px',
    textAlign: 'center',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #e0e0e0',
    borderTopColor: '#2563eb',
    borderRadius: '50%',
    margin: '0 auto 20px',
    animation: 'spin 1s linear infinite'
  },
  errorCard: {
    maxWidth: '500px',
    margin: '100px auto',
    padding: '40px',
    backgroundColor: '#fff5f5',
    borderRadius: '12px',
    textAlign: 'center',
    border: '1px solid #fed7d7'
  },
  successCard: {
    maxWidth: '500px',
    margin: '100px auto',
    padding: '40px',
    backgroundColor: '#f0fff4',
    borderRadius: '12px',
    textAlign: 'center',
    border: '1px solid #9ae6b4'
  },
  successIcon: {
    fontSize: '60px',
    marginBottom: '20px'
  },
  successSubtext: {
    color: '#666',
    marginTop: '10px'
  },
  companyInfo: {
    marginTop: '30px',
    padding: '20px',
    backgroundColor: 'white',
    borderRadius: '8px'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 24px',
    backgroundColor: '#1e3a8a',
    color: 'white',
    boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column'
  },
  preparerInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    backgroundColor: 'rgba(255,255,255,0.1)',
    padding: '8px 16px',
    borderRadius: '8px'
  },
  preparerLabel: {
    fontSize: '11px',
    opacity: 0.8,
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  preparerName: {
    fontSize: '14px',
    fontWeight: '600'
  },
  logo: {
    width: '50px',
    height: '50px',
    borderRadius: '8px',
    objectFit: 'cover',
    border: '2px solid rgba(255,255,255,0.3)'
  },
  title: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '700'
  },
  subtitle: {
    margin: '4px 0 0 0',
    fontSize: '14px',
    opacity: 0.9
  },
  progressSection: {
    padding: '16px 24px',
    backgroundColor: 'white',
    borderBottom: '1px solid #e2e8f0'
  },
  progressHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px'
  },
  progressLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#334155'
  },
  progressPercent: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#10b981'
  },
  progressTrack: {
    height: '8px',
    backgroundColor: '#e2e8f0',
    borderRadius: '4px',
    overflow: 'hidden'
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#10b981',
    borderRadius: '4px',
    transition: 'width 0.3s ease'
  },
  fieldSummary: {
    display: 'flex',
    gap: '24px',
    marginTop: '12px'
  },
  summaryItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#64748b'
  },
  summaryIcon: {
    fontSize: '14px'
  },
  legend: {
    display: 'flex',
    gap: '20px',
    padding: '10px 24px',
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
    fontSize: '12px',
    color: '#64748b'
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  legendDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%'
  },
  progressBar: {
    padding: '10px 20px',
    backgroundColor: 'white',
    borderBottom: '1px solid #e2e8f0'
  },
  progressInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '14px',
    color: '#64748b'
  },
  pdfContainer: {
    flex: 1,
    overflow: 'auto',
    padding: '20px',
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: '#64748b'
  },
  pdfWrapper: {
    position: 'relative',
    backgroundColor: 'white',
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
  },
  loading: {
    padding: '100px',
    textAlign: 'center',
    color: '#64748b'
  },
  fieldInput: {
    padding: '4px 8px',
    borderRadius: '4px',
    backgroundColor: 'rgba(255,255,255,0.95)',
    outline: 'none',
    boxSizing: 'border-box'
  },
  clearSigButton: {
    position: 'absolute',
    top: '2px',
    right: '2px',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px'
  },
  fieldHint: {
    position: 'absolute',
    bottom: '-18px',
    left: '0',
    fontSize: '10px',
    color: '#64748b',
    whiteSpace: 'nowrap'
  },
  bottomBar: {
    padding: '15px 20px',
    backgroundColor: 'white',
    borderTop: '1px solid #e2e8f0',
    boxShadow: '0 -2px 10px rgba(0,0,0,0.1)'
  },
  pageNav: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '10px'
  },
  navButton: {
    padding: '12px 24px',
    backgroundColor: '#e2e8f0',
    color: '#475569',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '500'
  },
  nextButton: {
    padding: '12px 24px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '600'
  },
  submitButton: {
    padding: '12px 24px',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '600',
    flex: 1
  },
  signatureModeToggle: {
    display: 'flex',
    gap: '4px',
    padding: '4px',
    backgroundColor: '#f1f5f9',
    borderRadius: '6px 6px 0 0',
    borderBottom: '1px solid #e2e8f0'
  },
  modeButton: {
    flex: 1,
    padding: '4px 8px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: '500',
    transition: 'all 0.2s ease'
  },
  // Signature Modal Styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: '20px'
  },
  signatureModal: {
    backgroundColor: 'white',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '480px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    overflow: 'hidden'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc'
  },
  modalTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b'
  },
  modalCloseBtn: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    color: '#94a3b8',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '4px'
  },
  modalTabs: {
    display: 'flex',
    padding: '12px 24px',
    gap: '8px',
    backgroundColor: '#f8fafc'
  },
  modalTab: {
    flex: 1,
    padding: '10px 16px',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    backgroundColor: '#e2e8f0',
    color: '#64748b',
    transition: 'all 0.2s ease'
  },
  modalTabActive: {
    backgroundColor: '#3b82f6',
    color: 'white'
  },
  modalContent: {
    padding: '24px'
  },
  modalDrawArea: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  modalCanvas: {
    border: '2px dashed #cbd5e1',
    borderRadius: '8px',
    cursor: 'crosshair',
    backgroundColor: '#fafafa',
    touchAction: 'none'
  },
  clearBtn: {
    alignSelf: 'flex-end',
    padding: '8px 16px',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500'
  },
  modalTypeArea: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  modalTypeInput: {
    width: '100%',
    padding: '14px 16px',
    fontSize: '16px',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box'
  },
  modalPreview: {
    padding: '20px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    textAlign: 'center',
    border: '1px solid #e2e8f0'
  },
  modalPreviewText: {
    fontFamily: "'Brush Script MT', 'Segoe Script', cursive",
    fontSize: '32px',
    color: '#1e40af'
  },
  modalActions: {
    display: 'flex',
    gap: '12px',
    padding: '20px 24px',
    borderTop: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc'
  },
  modalCancelBtn: {
    flex: 1,
    padding: '12px 20px',
    backgroundColor: '#e2e8f0',
    color: '#475569',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600'
  },
  modalDoneBtn: {
    flex: 1,
    padding: '12px 20px',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600'
  }
};

export default PDFDocumentFiller;
